import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { Badge } from '@/components/ui/badge'
import { 
  Users, 
  Mail, 
  Building2, 
  Shield, 
  UserPlus,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  X,
  Plus,
  Minus
} from 'lucide-react'
import { useBrands } from '@/lib/BrandsProvider'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type UserWithAccess = {
  user_id: string
  email: string
  name: string
  brands: {
    brand_id: string
    brand_name: string
    access_level: 'owner' | 'editor' | 'viewer' | 'member'
  }[]
}

type PlatformInvitation = {
  id: string
  email: string
  message?: string
  created_at: string
  expires_at: string
  inviter_email: string
  inviter_name: string
  brand_id: string
  brand_name?: string
}

const TeamManagement = () => {
  const { brands: readyBrands } = useBrands()
  const [users, setUsers] = useState<UserWithAccess[]>([])
  const [invitations, setInvitations] = useState<PlatformInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteMessage, setInviteMessage] = useState('')
  const [selectedBrandId, setSelectedBrandId] = useState('')
  const [selectedAccessLevel, setSelectedAccessLevel] = useState<'owner' | 'editor' | 'viewer'>('viewer')
  const [ownedBrands, setOwnedBrands] = useState<Array<{brand_id: string, brand_name: string, access_type: string}>>([])
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set())
  const [sendingInvite, setSendingInvite] = useState(false)
  const [cancellingInvitations, setCancellingInvitations] = useState<Set<string>>(new Set())
  const [grantingAccess, setGrantingAccess] = useState<Map<string, Set<string>>>(new Map())
  const [revokingAccess, setRevokingAccess] = useState<Map<string, Set<string>>>(new Map())
  const [updatingAccess, setUpdatingAccess] = useState<Map<string, Set<string>>>(new Map())
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const { toast } = useToast()

  // Fetch all users with brand access
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        setCurrentUserId(user.id)
      }
      await fetchOwnedBrands()
      fetchUsersWithAccess()
      fetchPlatformInvitations()
    }
    init()
  }, [])

  const fetchUsersWithAccess = async () => {
    try {
      setLoading(true)
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Get all brands the current user has access to
      const { data: brands, error: brandsError } = await supabase
        .from('brands')
        .select('id, brand_name, account_id')
        .neq('status', 'DELETED')
        .order('brand_name')

      if (brandsError) throw brandsError

      // For each brand, get users with access
      const userMap = new Map<string, UserWithAccess>()

      for (const brand of brands || []) {
        // Get direct access users for this brand
        const { data: directAccess } = await supabase
          .from('brand_access')
          .select('user_id, access_level')
          .eq('brand_id', brand.id)

        // Get account users if brand has account
        let accountUsers: any[] = []
        
        if (brand.account_id) {
          // Get account users
          const { data: accountUserData } = await supabase
            .schema('basejump')
            .from('account_user')
            .select('user_id, account_role')
            .eq('account_id', brand.account_id)
          
          accountUsers = accountUserData || []
        }

        // Combine users
        const allUserIds = new Set([
          ...(directAccess?.map(a => a.user_id) || []),
          ...(accountUsers.map(u => u.user_id))
        ])

        // Get user details from profiles table with email from marketing_and_customer_success schema
        if (allUserIds.size > 0) {
          // Fetch user profiles from marketing_and_customer_success schema which includes email
          const { data: marketingProfiles } = await supabase
            .schema('marketing_and_customer_success')
            .from('profiles')
            .select('id, email, full_name')
            .in('id', Array.from(allUserIds))
          
          // Also fetch from public profiles for additional data
          const { data: publicProfiles } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', Array.from(allUserIds))
          
          // Get emails from auth system for users without email in marketing profiles
          const emailMap = new Map<string, string>()
          for (const userId of allUserIds) {
            const marketingProfile = marketingProfiles?.find(p => p.id === userId)
            if (!marketingProfile?.email) {
              // Use RPC to get email from auth.users
              const { data: email } = await supabase.rpc('get_user_email', { user_id: userId })
              if (email) {
                emailMap.set(userId, email)
              }
            }
          }
          
          allUserIds.forEach(userId => {
            const marketingProfile = marketingProfiles?.find(p => p.id === userId)
            const publicProfile = publicProfiles?.find(p => p.id === userId)
            
            // Get email from marketing profile, email map, or empty string
            const email = marketingProfile?.email || emailMap.get(userId) || ''
            
            // Skip if we don't have either profile data or email
            if (!marketingProfile && !publicProfile && !email) return

            const directAccessRecord = directAccess?.find(a => a.user_id === userId)
            const accountUser = accountUsers.find(u => u.user_id === userId)

            // Determine access level
            let accessLevel = 'member'
            if (directAccessRecord) {
              accessLevel = directAccessRecord.access_level
            } else if (accountUser) {
              accessLevel = accountUser.account_role
            }

            // Determine display name
            const fullName = marketingProfile?.full_name || 
                           (publicProfile?.first_name && publicProfile?.last_name ? `${publicProfile.first_name} ${publicProfile.last_name}` : null) ||
                           publicProfile?.first_name ||
                           publicProfile?.last_name ||
                           email || 
                           'Unknown User'

            // Add or update user in map
            if (!userMap.has(userId)) {
              userMap.set(userId, {
                user_id: userId,
                email: email,
                name: fullName,
                brands: []
              })
            }

            const userEntry = userMap.get(userId)!
            userEntry.brands.push({
              brand_id: brand.id,
              brand_name: brand.brand_name,
              access_level: accessLevel as any
            })
          })
        }
      }

      setUsers(Array.from(userMap.values()).sort((a, b) => a.email.localeCompare(b.email)))
    } catch (error) {
      console.error('Error fetching users:', error)
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchOwnedBrands = async () => {
    try {
      const { data: owned, error } = await supabase
        .rpc('get_user_owned_brands')
      
      if (error) {
        console.error('Error fetching owned brands:', error)
        return
      }
      
      // Deduplicate brands by brand_id
      const brandsArray = Array.isArray(owned) ? owned : []
      const uniqueBrands = brandsArray.length > 0 ? Array.from(
        new Map(brandsArray.map((brand: any) => [brand.brand_id, brand])).values()
      ) as Array<{brand_id: string, brand_name: string, access_type: string}> : []
      
      setOwnedBrands(uniqueBrands)
      // Set the first brand as default if available
      if (uniqueBrands.length > 0) {
        setSelectedBrandId(uniqueBrands[0].brand_id)
      }
    } catch (error) {
      console.error('Error in fetchOwnedBrands:', error)
    }
  }

  const fetchPlatformInvitations = async () => {
    try {
      // For now, we'll fetch brand invitations from all brands
      // In the future, you might want a separate platform_invitations table
      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const session = await supabase.auth.getSession()
      
      if (!session.data.session) return

      const allInvitations: PlatformInvitation[] = []
      
      // Fetch invitations for all brands user has access to
      for (const brand of readyBrands) {
        try {
          const response = await fetch(`${workerUrl}/rainfactory/brands/${brand.id}/invitations`, {
            headers: {
              'Authorization': `Bearer ${session.data.session!.access_token}`
            }
          })
          
          if (response.ok) {
            const { data } = await response.json()
            if (data && Array.isArray(data)) {
              // Add invitations with brand info
              data.forEach((inv: any) => {
                allInvitations.push({
                  id: inv.id,
                  email: inv.email,
                  message: inv.message,
                  created_at: inv.created_at,
                  expires_at: inv.expires_at,
                  inviter_email: inv.inviter_email,
                  inviter_name: inv.inviter_name,
                  brand_id: brand.id,
                  brand_name: brand.brand_name
                })
              })
            }
          }
        } catch (error) {
          console.error(`Error fetching invitations for brand ${brand.id}:`, error)
        }
      }
      
      setInvitations(allInvitations)
    } catch (error) {
      console.error('Error fetching invitations:', error)
    }
  }

  const cancelInvitation = async (invitation: PlatformInvitation) => {
    setCancellingInvitations(prev => new Set(prev).add(invitation.id))
    
    try {
      const session = await supabase.auth.getSession()
      if (!session.data.session) return

      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const response = await fetch(
        `${workerUrl}/rainfactory/brands/${invitation.brand_id}/invitations/${invitation.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.data.session.access_token}`
          }
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to cancel invitation')
      }

      toast({
        title: 'Success',
        description: `Invitation to ${invitation.email} has been cancelled.`
      })

      // Remove from local state immediately for better UX
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id))
      
      // Refresh data after a short delay
      setTimeout(() => {
        fetchPlatformInvitations()
      }, 500)
    } catch (error) {
      console.error('Error cancelling invitation:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to cancel invitation',
        variant: 'destructive'
      })
    } finally {
      setCancellingInvitations(prev => {
        const newSet = new Set(prev)
        newSet.delete(invitation.id)
        return newSet
      })
    }
  }

  const sendInvitation = async () => {
    if (!inviteEmail) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        variant: 'destructive'
      })
      return
    }

    if (!selectedBrandId) {
      toast({
        title: 'Error',
        description: 'Please select a brand to grant initial access to',
        variant: 'destructive'
      })
      return
    }

    setSendingInvite(true)
    try {
      const session = await supabase.auth.getSession()
      if (!session.data.session) return

      const selectedBrand = ownedBrands.find(b => b.brand_id === selectedBrandId)
      if (!selectedBrand) {
        toast({
          title: 'Error',
          description: 'Selected brand not found',
          variant: 'destructive'
        })
        return
      }

      // Use the existing brand invitation endpoint
      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const response = await fetch(`${workerUrl}/rainfactory/brands/${selectedBrandId}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({
          action: 'create-invitation', // Required by middleware
          email: inviteEmail,
          access_level: selectedAccessLevel,
          message: inviteMessage || `You've been invited to join Rainfactory. After signing up, you'll be granted access to collaborate on various brands.`
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to send invitation')
      }

      toast({
        title: 'Success',
        description: `Invitation sent to ${inviteEmail}. They will have ${selectedAccessLevel} access to ${selectedBrand.brand_name}.`
      })

      // Reset form
      setInviteEmail('')
      setInviteMessage('')
      setSelectedAccessLevel('viewer')
      setShowInviteDialog(false)
      
      // Refresh data
      setTimeout(() => {
        fetchUsersWithAccess()
        fetchPlatformInvitations()
      }, 1000)
    } catch (error) {
      console.error('Error sending invitation:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send invitation',
        variant: 'destructive'
      })
    } finally {
      setSendingInvite(false)
    }
  }

  const toggleUserExpanded = (userId: string) => {
    const newExpanded = new Set(expandedUsers)
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId)
    } else {
      newExpanded.add(userId)
    }
    setExpandedUsers(newExpanded)
  }

  const handleOpenInviteDialog = async (open: boolean) => {
    setShowInviteDialog(open)
    if (open) {
      // Fetch owned brands when opening the dialog
      await fetchOwnedBrands()
    } else {
      // Reset form when closing
      setInviteEmail('')
      setInviteMessage('')
      setSelectedAccessLevel('viewer')
      setSelectedBrandId(ownedBrands[0]?.brand_id || '')
    }
  }

  // Grant access to a brand
  const grantAccessToBrand = async (userId: string, brandId: string, accessLevel: string = 'viewer') => {
    setGrantingAccess(prev => {
      const newMap = new Map(prev)
      const userSet = newMap.get(userId) || new Set()
      userSet.add(brandId)
      newMap.set(userId, userSet)
      return newMap
    })

    try {
      const session = await supabase.auth.getSession()
      if (!session.data.session) return

      const brand = ownedBrands.find(b => b.brand_id === brandId)
      if (!brand) return

      const user = users.find(u => u.user_id === userId)
      if (!user) return

      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const response = await fetch(`${workerUrl}/rainfactory/brands/${brandId}/access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({
          action: 'grant-access', // Add action for middleware
          email: user.email,
          access_level: accessLevel
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to grant access')
      }

      toast({
        title: 'Success',
        description: `Granted ${user.name} access to ${brand.brand_name}`
      })

      // Refresh the users list
      await fetchUsersWithAccess()
    } catch (error) {
      console.error('Error granting access:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to grant access',
        variant: 'destructive'
      })
    } finally {
      setGrantingAccess(prev => {
        const newMap = new Map(prev)
        const userSet = newMap.get(userId)
        if (userSet) {
          userSet.delete(brandId)
          if (userSet.size === 0) {
            newMap.delete(userId)
          } else {
            newMap.set(userId, userSet)
          }
        }
        return newMap
      })
    }
  }

  // Revoke access from a brand
  const revokeAccessFromBrand = async (userId: string, brandId: string) => {
    setRevokingAccess(prev => {
      const newMap = new Map(prev)
      const userSet = newMap.get(userId) || new Set()
      userSet.add(brandId)
      newMap.set(userId, userSet)
      return newMap
    })

    try {
      const session = await supabase.auth.getSession()
      if (!session.data.session) return

      const brand = ownedBrands.find(b => b.brand_id === brandId)
      if (!brand) return

      const user = users.find(u => u.user_id === userId)
      if (!user) return

      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const response = await fetch(`${workerUrl}/rainfactory/brands/${brandId}/access/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.data.session.access_token}`
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to revoke access')
      }

      toast({
        title: 'Success',
        description: `Revoked ${user.name}'s access to ${brand.brand_name}`
      })

      // Refresh the users list
      await fetchUsersWithAccess()
    } catch (error) {
      console.error('Error revoking access:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to revoke access',
        variant: 'destructive'
      })
    } finally {
      setRevokingAccess(prev => {
        const newMap = new Map(prev)
        const userSet = newMap.get(userId)
        if (userSet) {
          userSet.delete(brandId)
          if (userSet.size === 0) {
            newMap.delete(userId)
          } else {
            newMap.set(userId, userSet)
          }
        }
        return newMap
      })
    }
  }

  // Update access level for a brand
  const updateAccessLevel = async (userId: string, brandId: string, newLevel: string) => {
    setUpdatingAccess(prev => {
      const newMap = new Map(prev)
      const userSet = newMap.get(userId) || new Set()
      userSet.add(brandId)
      newMap.set(userId, userSet)
      return newMap
    })

    try {
      const session = await supabase.auth.getSession()
      if (!session.data.session) return

      const brand = ownedBrands.find(b => b.brand_id === brandId)
      if (!brand) return

      const user = users.find(u => u.user_id === userId)
      if (!user) return

      const workerUrl = import.meta.env.VITE_WORKERS_URL
      const response = await fetch(`${workerUrl}/rainfactory/brands/${brandId}/access/${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify({
          access_level: newLevel
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update access level')
      }

      toast({
        title: 'Success',
        description: `Updated ${user.name}'s access level to ${newLevel} for ${brand.brand_name}`
      })

      // Refresh the users list
      await fetchUsersWithAccess()
    } catch (error) {
      console.error('Error updating access level:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update access level',
        variant: 'destructive'
      })
    } finally {
      setUpdatingAccess(prev => {
        const newMap = new Map(prev)
        const userSet = newMap.get(userId)
        if (userSet) {
          userSet.delete(brandId)
          if (userSet.size === 0) {
            newMap.delete(userId)
          } else {
            newMap.set(userId, userSet)
          }
        }
        return newMap
      })
    }
  }

  if (loading && users.length === 0) {
    return <div className="flex items-center justify-center p-8">Loading users...</div>
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>
            View all users in your organization and their brand access. To manage access for a specific brand, visit that brand's settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Users section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Platform Users ({users.length})
                </h3>
                <Dialog open={showInviteDialog} onOpenChange={handleOpenInviteDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Invite New User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Invite User to Rainfactory</DialogTitle>
                      <DialogDescription>
                        Send an invitation for a new user to join the platform. Select which brand they should have initial access to.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="colleague@example.com"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="brand">Initial Brand Access</Label>
                        <Select value={selectedBrandId} onValueChange={setSelectedBrandId}>
                          <SelectTrigger id="brand">
                            <SelectValue placeholder="Select a brand" />
                          </SelectTrigger>
                          <SelectContent>
                            {ownedBrands.length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground">
                                You need to be an owner of at least one brand to invite users.
                              </div>
                            ) : (
                              ownedBrands.map((brand) => (
                                <SelectItem key={brand.brand_id} value={brand.brand_id}>
                                  {brand.brand_name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="access-level">Access Level</Label>
                        <Select value={selectedAccessLevel} onValueChange={(value) => setSelectedAccessLevel(value as 'owner' | 'editor' | 'viewer')}>
                          <SelectTrigger id="access-level">
                            <SelectValue placeholder="Select access level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {selectedAccessLevel === 'owner' && 'Full control over the brand including user management'}
                          {selectedAccessLevel === 'editor' && 'Can view and edit brand content but cannot manage users'}
                          {selectedAccessLevel === 'viewer' && 'Can view brand content but cannot make changes'}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="message">Message (optional)</Label>
                        <Textarea
                          id="message"
                          placeholder="Add a personal message to the invitation..."
                          value={inviteMessage}
                          onChange={(e) => setInviteMessage(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setShowInviteDialog(false)}
                        disabled={sendingInvite}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={sendInvitation} 
                        disabled={sendingInvite || !inviteEmail || !selectedBrandId || ownedBrands.length === 0}
                      >
                        {sendingInvite ? 'Sending...' : 'Send Invitation'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {users.length === 0 ? (
                <div className="text-center py-8 border rounded-lg">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No users yet.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Invite users to collaborate on your brands.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <Collapsible 
                      key={user.user_id}
                      open={expandedUsers.has(user.user_id)}
                      onOpenChange={() => toggleUserExpanded(user.user_id)}
                    >
                      <div className="border rounded-lg">
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-3">
                              {expandedUsers.has(user.user_id) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                              <div className="text-left">
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                            <Badge variant="outline">
                              {user.brands.length} brand{user.brands.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <div className="px-4 pb-4 pt-2 space-y-2">
                            <p className="text-sm font-medium text-muted-foreground mb-2">Brand Access:</p>
                            {user.brands.length === 0 ? (
                              <p className="text-sm text-muted-foreground italic">No brand access</p>
                            ) : (
                              user.brands.map((brand) => (
                                <div 
                                  key={brand.brand_id}
                                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-md"
                                >
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{brand.brand_name}</p>
                                  </div>
                                  <Badge 
                                    variant={brand.access_level === 'owner' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {brand.access_level === 'member' ? 'editor' : brand.access_level}
                                  </Badge>
                                </div>
                              ))
                            )}
                            {/* Brand Access Management Actions */}
                            {ownedBrands.length > 0 && user.user_id !== currentUserId && (
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-sm font-medium text-muted-foreground mb-2">Manage Access for Your Brands:</p>
                                <div className="space-y-2">
                                  {ownedBrands.map((ownedBrand) => {
                                    const userBrandAccess = user.brands.find(b => b.brand_id === ownedBrand.brand_id)
                                    const userHasAccess = !!userBrandAccess
                                    const isGranting = grantingAccess.get(user.user_id)?.has(ownedBrand.brand_id)
                                    const isRevoking = revokingAccess.get(user.user_id)?.has(ownedBrand.brand_id)
                                    const isUpdating = updatingAccess.get(user.user_id)?.has(ownedBrand.brand_id)
                                    
                                    return (
                                      <div 
                                        key={ownedBrand.brand_id}
                                        className="flex items-center justify-between p-2 rounded-md bg-muted/30"
                                      >
                                        <div className="flex items-center gap-2 flex-1">
                                          <Building2 className="h-3 w-3 text-muted-foreground" />
                                          <span className="text-sm">{ownedBrand.brand_name}</span>
                                        </div>
                                        {userHasAccess ? (
                                          <div className="flex items-center gap-2">
                                            <Select
                                              value={userBrandAccess.access_level}
                                              onValueChange={(value) => updateAccessLevel(user.user_id, ownedBrand.brand_id, value)}
                                              disabled={isUpdating}
                                            >
                                              <SelectTrigger className="h-7 w-24 text-xs">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="owner">Owner</SelectItem>
                                                <SelectItem value="editor">Editor</SelectItem>
                                                <SelectItem value="viewer">Viewer</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-7 text-xs"
                                              onClick={() => revokeAccessFromBrand(user.user_id, ownedBrand.brand_id)}
                                              disabled={isRevoking}
                                            >
                                              {isRevoking ? (
                                                <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                                              ) : (
                                                <>
                                                  <Minus className="h-3 w-3 mr-1" />
                                                  Revoke
                                                </>
                                              )}
                                            </Button>
                                          </div>
                                        ) : (
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 text-xs"
                                                disabled={isGranting}
                                              >
                                                {isGranting ? (
                                                  <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                                                ) : (
                                                  <>
                                                    <Plus className="h-3 w-3 mr-1" />
                                                    Grant Access
                                                  </>
                                                )}
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                              <DropdownMenuLabel className="text-xs">Select Access Level</DropdownMenuLabel>
                                              <DropdownMenuSeparator />
                                              <DropdownMenuItem 
                                                className="text-xs"
                                                onClick={() => grantAccessToBrand(user.user_id, ownedBrand.brand_id, 'owner')}
                                              >
                                                Owner
                                              </DropdownMenuItem>
                                              <DropdownMenuItem 
                                                className="text-xs"
                                                onClick={() => grantAccessToBrand(user.user_id, ownedBrand.brand_id, 'editor')}
                                              >
                                                Editor
                                              </DropdownMenuItem>
                                              <DropdownMenuItem 
                                                className="text-xs"
                                                onClick={() => grantAccessToBrand(user.user_id, ownedBrand.brand_id, 'viewer')}
                                              >
                                                Viewer
                                              </DropdownMenuItem>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Invitations */}
            {invitations.length > 0 && (
              <div>
                <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
                  <Mail className="h-5 w-5" />
                  Pending Invitations ({invitations.length})
                </h3>
                
                <div className="space-y-2">
                  {invitations.map((invitation) => (
                    <div 
                      key={invitation.id}
                      className="flex items-center justify-between p-4 border rounded-lg bg-amber-50 dark:bg-amber-900/20"
                    >
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                        <div>
                          <p className="font-medium">{invitation.email}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Invited by {invitation.inviter_name} • 
                            {invitation.brand_name && `To ${invitation.brand_name} • `}
                            Expires {new Date(invitation.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelInvitation(invitation)}
                        disabled={cancellingInvitations.has(invitation.id)}
                        className="h-8 w-8 p-0"
                        title="Cancel invitation"
                      >
                        {cancellingInvitations.has(invitation.id) ? (
                          <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            About User Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              • <strong>Platform Users:</strong> All users who have access to any brands in your organization.
            </p>
            <p>
              • <strong>Brand Access:</strong> Shows which brands each user can access and their role.
            </p>
            <p>
              • <strong>Managing Access:</strong> For brands you own, you can grant or revoke access directly from this page. For other brands, visit that brand's settings page.
            </p>
            <p>
              • <strong>Invitations:</strong> New users will receive an email invitation to join the platform. After signing up, you can grant them access to specific brands.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default TeamManagement

// Create a JSX file for the feature-protected route that can be used in routes
import { FeatureProtectedRoute } from '@/components/ui/feature-protected-route'

export const ProtectedTeamManagement = () => (
  <FeatureProtectedRoute featureName="teams">
    <TeamManagement />
  </FeatureProtectedRoute>
)